import { parseCurrency, toCents } from 'components/Widget/shared/currency';
import {
  ADDITIONAL_MEDICARE_RATE,
  ADDITIONAL_MEDICARE_THRESHOLD,
  CURRENT_TAX_YEAR,
  getPayFrequency,
  MEDICARE_RATE,
  SOCIAL_SECURITY_RATE,
  WEEKS_PER_YEAR,
} from './constants';
import type { FilingStatusKey, PaycheckInput, PaycheckResult } from './types';

const annualGrossInCents = (pay: PaycheckInput['pay']): number =>
  pay.payType === 'hourly'
    ? toCents(parseCurrency(pay.hourlyWage)) * parseCurrency(pay.weeklyHours) * WEEKS_PER_YEAR
    : toCents(parseCurrency(pay.annualSalary));

const annualFederalInCents = (grossInCents: number, filingStatus: FilingStatusKey): number => {
  const brackets = CURRENT_TAX_YEAR.brackets[filingStatus];
  const taxableInCents = Math.max(0, grossInCents - toCents(CURRENT_TAX_YEAR.standardDeduction[filingStatus]));

  return brackets.reduce((tax, bracket, index) => {
    const ceiling = brackets[index + 1]?.floor;
    const topInCents = ceiling === undefined ? taxableInCents : Math.min(taxableInCents, toCents(ceiling));

    return tax + Math.max(0, topInCents - toCents(bracket.floor)) * bracket.rate;
  }, 0);
};

const annualSocialSecurityInCents = (grossInCents: number): number =>
  Math.min(grossInCents, toCents(CURRENT_TAX_YEAR.socialSecurityWageBase)) * SOCIAL_SECURITY_RATE;

const annualMedicareInCents = (grossInCents: number): number =>
  grossInCents * MEDICARE_RATE +
  Math.max(0, grossInCents - toCents(ADDITIONAL_MEDICARE_THRESHOLD)) * ADDITIONAL_MEDICARE_RATE;

/**
 * With no year-to-date pay to go on, every line is the annual figure averaged across the year, so
 * a high earner's real stubs front-load Social Security and then stop at the wage base.
 */
export const calculatePaycheck = ({ payFrequency, pay, filingStatus, exemptions }: PaycheckInput): PaycheckResult => {
  const { periodsPerYear } = getPayFrequency(payFrequency);
  const annualInCents = annualGrossInCents(pay);
  const perPeriod = (annualAmountInCents: number): number => Math.round(annualAmountInCents / periodsPerYear);

  const gross = perPeriod(annualInCents);
  const federal = exemptions.federal ? 0 : perPeriod(annualFederalInCents(annualInCents, filingStatus));
  const socialSecurity = exemptions.socialSecurity ? 0 : perPeriod(annualSocialSecurityInCents(annualInCents));
  const medicare = exemptions.medicare ? 0 : perPeriod(annualMedicareInCents(annualInCents));

  return {
    gross,
    federal,
    socialSecurity,
    medicare,
    // Derived rather than rounded on its own, as a pay stub does, so the segments sum exactly to gross.
    takeHome: gross - federal - socialSecurity - medicare,
  };
};
